// ── PosImportModal — POS 매출 엑셀 일괄 가져오기 ──
// 사용자가 POS에서 다운받은 매출 리포트(xlsx)를 업로드 →
// 자동 파싱 → 미리보기 → Firestore에 일괄 저장

import React, { useState, useMemo, useRef } from 'react';
import {
  parsePosExcel,
  buildTransactionsFromPos,
  findExistingPosDates,
  formatKRW,
} from '../utils/posExcelMapper';
import { L } from '../i18n/L';   // V160 중국어

// 세이지+코랄 팔레트
const SAGE = '#EA580C';
const SAGE_DARK = '#2C3A30';
const SAGE_BG = '#E0EAE2';
const CORAL = '#F4A89A';
const CORAL_DARK = '#B55041';
const WARM_BG = '#F7F4ED';
const WHITE = '#FFFFFF';
const MUTED = '#6B7A71';

export default function PosImportModal({ onClose, addTransaction, transactions = [] }) {
  const [parsed, setParsed] = useState(null);     // { dayList, summary, ... }
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [skipExisting, setSkipExisting] = useState(true);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const existingDates = useMemo(
    () => findExistingPosDates(transactions, 'pos-import'),
    [transactions]
  );

  const dayListForSave = useMemo(() => {
    if (!parsed?.dayList) return [];
    if (!skipExisting) return parsed.dayList;
    return parsed.dayList.filter(d => !existingDates.has(d.date));
  }, [parsed, skipExisting, existingDates]);

  const skippedCount = (parsed?.dayList?.length || 0) - dayListForSave.length;

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setParsing(true);
    setParsed(null);
    setCompleted(false);
    try {
      const result = await parsePosExcel(file);
      setParsed(result);
    } catch (e) {
      setError(e.message || L("파싱 실패"));
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (dayListForSave.length === 0) {
      alert(L("저장할 날짜가 없습니다."));
      return;
    }
    if (!window.confirm(
      L("{0}일 분량의 매출을 거래장부에 추가합니다.\n", dayListForSave.length) +
      L("(이미 등록된 {0}일은 건너뜁니다)\n계속하시겠어요?", skippedCount)
    )) return;

    setSaving(true);
    const txs = buildTransactionsFromPos(
      { ...parsed, dayList: dayListForSave },
      { source: 'pos-import' }
    );
    setProgress({ done: 0, total: txs.length });

    let success = 0, failed = 0;
    for (let i = 0; i < txs.length; i++) {
      try {
        await addTransaction(txs[i]);
        success++;
      } catch (e) {
        console.error('POS import failed at tx', i, e);
        failed++;
      }
      setProgress({ done: i + 1, total: txs.length });
    }

    setSaving(false);
    setCompleted(true);
    alert(L("POS 가져오기 완료\n성공: {0}건 / 실패: {1}건", success, failed));
  };

  return (
    <Modal onClose={completed || !saving ? onClose : undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: SAGE_DARK }}>
          {L("📊 POS 매출 엑셀 가져오기")}
        </h3>
        <button onClick={onClose} style={closeBtnStyle}>×</button>
      </div>

      {/* 안내 */}
      <div style={{
        padding: '10px 14px', background: WARM_BG, borderRadius: 10,
        marginBottom: 14, fontSize: 12, color: SAGE_DARK, lineHeight: 1.6,
      }}>
        {L("💡 POS 시스템(테이블오더·키오스크 등)에서 다운받은")} <strong>{L("매출 리포트 xlsx")}</strong>{L("를 그대로 업로드하면 자동으로 일별·메뉴별 매출이 등록돼요.")}
        <br />
        {L("⚠️ 비밀번호 보호된 파일은 엑셀에서 해제 후 저장하고 업로드해주세요.")}
      </div>

      {/* 파일 업로드 */}
      {!parsed && !parsing && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleFile(file);
          }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: '40px 20px', textAlign: 'center', cursor: 'pointer',
            border: `2px dashed ${dragOver ? SAGE : '#D1D5DB'}`,
            borderRadius: 12,
            background: dragOver ? SAGE_BG : '#FAFAF7',
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 8 }}>📁</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: SAGE_DARK, marginBottom: 4 }}>
            {L("엑셀 파일을 끌어다 놓거나 클릭해서 선택")}
          </div>
          <div style={{ fontSize: 11, color: MUTED }}>
            {L("지원 형식: .xlsx (POS 매출 리포트)")}
          </div>
          <input
            ref={fileInputRef} type="file" accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}

      {parsing && (
        <div style={{ padding: 40, textAlign: 'center', color: MUTED }}>
          {L("엑셀 파싱 중...")}
        </div>
      )}

      {error && (
        <div style={{
          padding: 12, background: '#FCEAE5', borderRadius: 8,
          fontSize: 13, color: CORAL_DARK, marginBottom: 12,
        }}>
          ❌ {error}
          <button
            onClick={() => { setError(''); setParsed(null); }}
            style={{
              marginLeft: 10, padding: '4px 10px', background: WHITE,
              border: `1px solid ${CORAL_DARK}`, borderRadius: 6,
              fontSize: 12, color: CORAL_DARK, cursor: 'pointer',
            }}
          >
            {L("다시 시도")}
          </button>
        </div>
      )}

      {/* 미리보기 */}
      {parsed && !completed && (
        <>
          <div style={{
            padding: 14, background: SAGE_BG, borderRadius: 10,
            marginBottom: 14,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: SAGE_DARK, marginBottom: 8 }}>
              {L("✅ 파싱 완료 — {0}", parsed.fileName)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, color: SAGE_DARK }}>
              <div>
                <span style={{ color: MUTED }}>{L("총 일수:")} </span>
                <strong>{L("{0}일", parsed.summary.totalDays)}</strong>
              </div>
              <div>
                <span style={{ color: MUTED }}>{L("총 매출:")} </span>
                <strong>₩{formatKRW(parsed.summary.totalRevenue)}</strong>
              </div>
              <div>
                <span style={{ color: MUTED }}>{L("메뉴 행:")} </span>
                <strong>{L("{0}건", parsed.summary.totalMenus)}</strong>
              </div>
              <div>
                <span style={{ color: MUTED }}>{L("기간:")} </span>
                <strong>
                  {parsed.summary.dateRange?.start} ~ {parsed.summary.dateRange?.end}
                </strong>
              </div>
            </div>
          </div>

          {/* 중복 처리 */}
          {existingDates.size > 0 && (
            <div style={{
              padding: 12, background: '#FFFBEB', borderRadius: 8,
              fontSize: 12, color: '#92400E', marginBottom: 14,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <input
                type="checkbox" id="skip-existing"
                checked={skipExisting}
                onChange={(e) => setSkipExisting(e.target.checked)}
                style={{ accentColor: SAGE }}
              />
              <label htmlFor="skip-existing" style={{ cursor: 'pointer', flex: 1 }}>
                {L("이미 POS 가져오기로 등록된 {0}일은 건너뛰기", existingDates.size)}
                {skipExisting && skippedCount > 0 && (
                  <span style={{ marginLeft: 6 }}>
                    {L("(실제 저장:")} <strong>{L("{0}일", dayListForSave.length)}</strong>)
                  </span>
                )}
              </label>
            </div>
          )}

          {/* Top 10 일자 미리보기 */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, marginBottom: 6 }}>
              {L("미리보기 (최근 10일):")}
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', background: WHITE, borderRadius: 8, border: '0.5px solid #E5E7EB' }}>
              {dayListForSave.slice(0, 10).map((d, i) => {
                const total = d.payment?.total || 0;
                const menuCount = d.lineItems?.length || 0;
                return (
                  <div key={d.date} style={{
                    padding: '8px 12px',
                    borderBottom: i < 9 ? '0.5px solid #F3F4F6' : 'none',
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 12, alignItems: 'center',
                  }}>
                    <span style={{ fontWeight: 600, color: SAGE_DARK }}>{d.date}</span>
                    <span style={{ color: MUTED }}>
                      ₩{formatKRW(total)} {L("· {0}종 메뉴", menuCount)}
                    </span>
                  </div>
                );
              })}
              {dayListForSave.length > 10 && (
                <div style={{ padding: '6px 12px', textAlign: 'center', fontSize: 11, color: MUTED }}>
                  {L("... 외 {0}일", dayListForSave.length - 10)}
                </div>
              )}
            </div>
          </div>

          {saving && (
            <div style={{
              padding: 12, background: SAGE_BG, borderRadius: 8,
              fontSize: 13, color: SAGE_DARK, marginBottom: 14,
            }}>
              {L("저장 중... {0}/{1}건", progress.done, progress.total)}
              <div style={{ marginTop: 6, height: 4, background: WHITE, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', background: SAGE,
                  width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
                  transition: 'width 0.2s',
                }} />
              </div>
            </div>
          )}

          {/* 저장 버튼 */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setParsed(null); setError(''); }}
              disabled={saving}
              style={{
                padding: '12px 20px', background: WHITE, color: MUTED,
                border: '1.5px solid #E5E7EB', borderRadius: 10,
                fontSize: 14, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
              }}
            >
              {L("다른 파일 선택")}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || dayListForSave.length === 0}
              style={{
                flex: 1, padding: '12px', background: SAGE, color: WHITE,
                border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: saving || dayListForSave.length === 0 ? 'not-allowed' : 'pointer',
                opacity: saving || dayListForSave.length === 0 ? 0.5 : 1,
              }}
            >
              {saving ? L("저장 중...") : L("{0}일 매출 일괄 저장", dayListForSave.length)}
            </button>
          </div>
        </>
      )}

      {completed && (
        <div style={{
          padding: 22, textAlign: 'center', background: '#FAFAFA',
          borderRadius: 12, marginBottom: 12,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#18181B', marginBottom: 6 }}>
            {L("가져오기 완료")}
          </div>
          <div style={{ fontSize: 12, color: '#71717A' }}>
            {L("홈 대시보드 또는 거래장부에서 확인하세요.")}
          </div>
          <button
            onClick={onClose}
            style={{
              marginTop: 14, padding: '10px 24px', background: SAGE,
              color: WHITE, border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {L("닫기")}
          </button>
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────── 공통 ─────────────────────────

function Modal({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: WHITE, borderRadius: 14, padding: 22,
          maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

const closeBtnStyle = {
  width: 32, height: 32, borderRadius: '50%',
  background: '#F3F4F6', border: 'none', color: MUTED,
  fontSize: 18, cursor: 'pointer',
};
